// src/pages/projects/Projects.jsx
import React, { useCallback, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  AssignmentTurnedIn as TasksIcon,
  Group as TeamIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';

// Import components
import EntityListContainer from '../../components/common/EntityListContainer';
import { EntityStatusBadge } from '../../components/core';
import { ProjectCard } from '../../components/core/cards';
import useConfirmDialog from '../../hooks/useConfirmationDialog';

// Import stores
import useAuthStore from '../../stores/authStore';
import useProjectStore from '../../stores/projectStore';
import useClientStore from '../../stores/clientStore';
import useNotificationStore from '../../stores/notificationStore';

// Filter configuration
const FILTER_CONFIG = [
  {
    name: 'status',
    label: 'Status',
    allLabel: 'All Statuses',
    options: [
      { value: 'draft', label: 'Draft' },
      { value: 'planning', label: 'Planning' },
      { value: 'active', label: 'Active' },
      { value: 'on_hold', label: 'On Hold' },
      { value: 'completed', label: 'Completed' },
      { value: 'cancelled', label: 'Cancelled' }
    ]
  },
  {
    name: 'priority',
    label: 'Priority',
    options: [
      { value: 'low', label: 'Low' },
      { value: 'medium', label: 'Medium' },
      { value: 'high', label: 'High' },
      { value: 'urgent', label: 'Urgent' }
    ],
    allLabel: 'All Priorities'
  }
];

// Sort options
const SORT_OPTIONS = [
  { field: 'name', label: 'Name' },
  { field: 'updatedAt', label: 'Last Updated' },
  { field: 'createdAt', label: 'Date Created' },
  { field: 'dates.plannedStartDate', label: 'Start Date' },
  { field: 'dates.plannedEndDate', label: 'End Date' }
];

/**
 * Projects listing page
 */
const Projects = () => {
  const navigate = useNavigate();
  
  // Hooks
  const { hasPermission } = useAuthStore();
  const { projects, meta, isLoading, error, fetchProjects, deleteProject } = useProjectStore();
  const { clients, fetchClients } = useClientStore();
  const { showSuccess, showError } = useNotificationStore();
  const [ConfirmDialog, confirm] = useConfirmDialog();
  
  // Load client data for filtering
  React.useEffect(() => {
    if (!clients.length) {
      fetchClients();
    }
  }, [fetchClients]);
  
  // Update filter config with clients
  const completeFilterConfig = React.useMemo(() => {
    const clientConfig = {
      name: 'clientId',
      label: 'Client',
      options: clients.map(client => ({
        value: client._id,
        label: client.name
      })),
      allLabel: 'All Clients'
    };
    
    return [...FILTER_CONFIG, clientConfig];
  }, [clients]);
  
  // Check permissions
  const canCreate = hasPermission('projects', 'create');
  const canUpdate = hasPermission('projects', 'update');
  const canDelete = hasPermission('projects', 'delete');

  // Handle delete project with confirmation
  const handleDeleteProject = useCallback(async (project) => {
    const confirmed = await confirm({
      title: `Delete ${project.name}?`,
      content: `This will permanently delete the project and all associated tasks. This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      isDelete: true
    });
    
    if (confirmed) {
      try {
        await deleteProject(project._id);
        showSuccess(`Project "${project.name}" deleted successfully`);
      } catch (error) {
        showError(`Failed to delete project: ${error.message}`);
      }
    }
  }, [confirm, deleteProject, showSuccess, showError]);

  // Format date helper
  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return 'Invalid date';
    }
  };

  // Calculate project progress
  const calculateProgress = (project) => {
    if (project.status === 'completed') return 100;
    if (project.status === 'draft') return 0;
    
    if (project.milestones?.length > 0) {
      const completedMilestones = project.milestones.filter(m => m.status === 'completed').length;
      return Math.round((completedMilestones / project.milestones.length) * 100);
    }
    
    // Default progress based on status
    const progressMap = { planning: 10, active: 50, on_hold: project.progress || 30 };
    return progressMap[project.status] || 0;
  };
  
  // Get client name by ID
  const getClientName = (clientId) => {
    if (!clientId) return 'No Client';
    if (typeof clientId === 'object' && clientId.name) {
      return clientId.name;
    }
    const client = clients.find(c => c._id === clientId);
    return client ? client.name : 'Unknown Client';
  };

  // Entity renderer for grid view
  const renderProject = useCallback((project) => {
    return (
      <ProjectCard
        project={project}
        calculateProgress={() => calculateProgress(project)}
        getClientName={() => getClientName(project.clientId)}
        primaryAction={{
          label: 'View',
          icon: <ViewIcon />,
          onClick: () => navigate(`/projects/${project._id}`),
          variant: 'contained',
          color: 'primary'
        }}
        onEdit={canUpdate ? () => navigate(`/projects/${project._id}/edit`) : undefined}
        onDelete={canDelete ? () => handleDeleteProject(project) : undefined}
        onManageTasks={() => navigate(`/projects/${project._id}/tasks`)}
        onManageTeam={() => navigate(`/projects/${project._id}/team`)}
        onManageBudget={() => navigate(`/projects/${project._id}/budget`)}
        linkTo={`/projects/${project._id}`}
      />
    );
  }, [navigate, getClientName, calculateProgress, canUpdate, canDelete, handleDeleteProject]);

  // Define table columns
  const tableColumns = [
    { 
      id: 'name', 
      label: 'Project Name',
      renderCell: (project) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="body1" fontWeight={500}>
            {project.name}
          </Typography>
        </Box>
      )
    },
    { 
      id: 'status', 
      label: 'Status',
      align: 'center',
      renderCell: (project) => (
        <EntityStatusBadge status={project.status} entityType="project" size="small" />
      )
    },
    { 
      id: 'client', 
      label: 'Client',
      align: 'left',
      renderCell: (project) => (
        <Typography variant="body2">
          {getClientName(project.clientId)}
        </Typography>
      )
    },
    { 
      id: 'dates', 
      label: 'Timeline',
      align: 'center',
      renderCell: (project) => (
        <Typography variant="body2">
          {formatDate(project.dates?.plannedStartDate)} - {formatDate(project.dates?.plannedEndDate)}
        </Typography>
      )
    },
    { 
      id: 'progress', 
      label: 'Progress',
      align: 'center',
      renderCell: (project) => (
        <Typography variant="body2">
          {calculateProgress(project)}%
        </Typography>
      )
    }
  ];

  // Define row actions for table view
  const getRowActions = useCallback((project) => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
      <Tooltip title="Tasks">
        <IconButton 
          size="small" 
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/projects/${project._id}/tasks`);
          }}
        >
          <TasksIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      
      <Tooltip title="Team">
        <IconButton 
          size="small" 
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/projects/${project._id}/team`);
          }}
        >
          <TeamIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      
      {canUpdate && (
        <Tooltip title="Edit">
          <IconButton 
            size="small" 
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/projects/${project._id}/edit`);
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      
      {canDelete && (
        <Tooltip title="Delete">
          <IconButton 
            size="small" 
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteProject(project);
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  ), [canUpdate, canDelete, navigate, handleDeleteProject]);

  return (
    <>
      <EntityListContainer
        title="Projects"
        description="Manage all your projects and their tasks"
        fetchData={fetchProjects}
        renderEntity={renderProject}
        initialFilters={{
          status: 'all',
          priority: 'all',
          clientId: 'all'
        }}
        initialSort={{ field: 'createdAt', direction: 'desc' }}
        filterConfig={completeFilterConfig}
        sortOptions={SORT_OPTIONS}
        
        // View options
        availableViews={['grid', 'table']}
        defaultView="grid"
        
        // Table configuration
        tableConfig={{
          columns: tableColumns,
          onRowClick: (project) => navigate(`/projects/${project._id}`),
          renderRowActions: getRowActions
        }}
        
        // Use direct store values
        entities={projects}
        isLoading={isLoading}
        error={error}
        metadata={meta}
        
        actions={{
          canCreate,
          createPath: '/projects/create',
          createLabel: 'New Project'
        }}
        emptyState={{
          entityType: 'project',
          // Customize if needed, but EntityEmptyState already has good defaults for projects
          customIcon: <TasksIcon sx={{ fontSize: 64 }} />
        }}
      />
      
      <ConfirmDialog />
    </>
  );
};

export default Projects;